import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { MagnifyingGlassIcon } from '@heroicons/react/24/solid';
import Styles from './customerStyle.module.scss';
import { FaTrash, FaEye, FaChevronDown, FaChevronRight, FaEyeSlash, FaDownload } from 'react-icons/fa';
import { getLatestReportByTenants } from '../../components/services/ReportService';
import { useAuth } from 'oidc-react';
import Project from '../../common/Utils/project';
import { LoaderCircle } from 'lucide-react';
import { useTenantSelection } from '../../providers/TenantSelectionProvider';
import api from '../../components/services/apiService';
import { useToast } from '../../providers/ToastProvider/ToastContext';
import { DirectoryTenant, getUserDirectoryTree } from '../../components/services/userDirectoryService';
import TenantRow, { getStatusColor } from './TenantRow';
import ResellerGroupBox from './ResellerGroupBox';
import { Tenant, groupTenantsByReseller, searchTenantGrouping, tenantMatchesSearch } from './tenantGroups';

const ROLES_ALLOWED_TO_CREATE = ['globaladmin', 'reseller'];
const ITEMS_PER_PAGE = 20;

const CustomerBase: React.FC = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [search, setSearch] = useState('');
  const { selectedTenantId, setSelectedTenantId } = useTenantSelection();
  const [executiveReportMap, setExecutiveReportMap] = useState<{ [tenantId: string]: any }>({});
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [tenant, setTenant] = useState<Tenant[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [expandedGlobal, setExpandedGlobal] = useState<boolean>(true);
  const auth = useAuth();
  // Privacy mode (tenant names masked) starts on for Globaladmin, who sees every customer, and off for a reseller,
  // who sees only its own customers. Other roles keep the previous default (on). The toggle changes it per visit.
  const [privacyMode, setPrivacyMode] = useState<boolean>(() => auth?.userData?.profile?.role !== 'reseller');
  const [tenantPrivacyOverrides, setTenantPrivacyOverrides] = useState<Record<string, boolean>>({});

  const navigate = useNavigate();
  const location = useLocation();
  const { addToast } = useToast();

  const tenantId = auth?.userData?.profile?.tenant_id;
  const role = auth?.userData?.profile?.role;
  // Only Globaladmin sees resellers alongside other tenants; a reseller's list is already just its own tenants.
  const groupByReseller = role === 'globaladmin';

  // Which tenants belong to which reseller (AuthService's directory tree); null = not loaded or failed.
  const [directory, setDirectory] = useState<DirectoryTenant | null>(null);
  const [directoryFailed, setDirectoryFailed] = useState(false);
  const [expandedResellers, setExpandedResellers] = useState<Set<string>>(new Set());

  // Identify root tenant by matching tenantId from auth with the list
  const rootTenant = tenant.find((t) => t.tenantId === tenantId);
  // Exclude root tenant from all aggregate/total calculations and display
  const regularTenants = tenant.filter((t) => t.tenantId !== tenantId);

  const filteredData = regularTenants.filter((item) => tenantMatchesSearch(item, search));

  // Grouped view (Globaladmin with the tree loaded): resellers in boxes, then the tenants no reseller manages.
  const grouping = groupByReseller && directory ? searchTenantGrouping(groupTenantsByReseller(regularTenants, directory), search) : null;

  // The paged list: tenants under no reseller in the grouped view, every tenant otherwise.
  const pagedTenants = grouping ? grouping.direct : filteredData;
  const totalPages = Math.ceil(pagedTenants.length / ITEMS_PER_PAGE);
  const currentData = pagedTenants.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);

  useEffect(() => {
    setCurrentPage(1);
  }, [search]);

  const getTenantList = async () => {
    setLoading(true);
    try {
      const apiUrl =
        role === 'globaladmin' || role === 'reseller'
          ? `${Project.api}tenantservice/tenant/dashboard/tenants`
          : `${Project.api}tenantservice/tenant/dashboard/tenants/${tenantId}`;

      // The tree is optional: if it fails, the page falls back to the flat list (the interceptor toasts the failure).
      const directoryRequest =
        groupByReseller && tenantId
          ? getUserDirectoryTree(tenantId as string).catch((error) => {
              console.error('Error fetching reseller mapping (user directory):', error);
              setDirectoryFailed(true);
              return null;
            })
          : Promise.resolve(null);

      const [response, tree] = await Promise.all([api.get(apiUrl), directoryRequest]);
      setTenant(response.data.data || []);
      setDirectory(tree);
    } catch (error) {
      console.error(`Error fetching groups:`, error);
    } finally {
      setLoading(false);
    }
  };

  // While searching, every reseller with matches is open; otherwise only the ones the user opened.
  const isResellerExpanded = (id: string) => !!search.trim() || expandedResellers.has(id);
  const toggleReseller = (id: string) =>
    setExpandedResellers((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  useEffect(() => {
    getTenantList();
  }, []);

  // Fetch executive report info for all tenants except Defatom global
  useEffect(() => {
    // Only run if tenants are loaded
    if (!tenant || tenant.length === 0) return;
    const tenantIds = tenant.filter((t) => !t.isGlobalTenant).map((t) => t.tenantId);
    if (tenantIds.length === 0) return;
    getLatestReportByTenants(tenantIds, 'ExecutiveReport').then((res) => {
      if (res.isSuccess && Array.isArray(res.data)) {
        const map: { [tenantId: string]: any } = {};
        res.data.forEach((item: any) => {
          if (item && item.metadata && item.metadata.tenantId) {
            map[item.metadata.tenantId] = item;
          }
        });
        setExecutiveReportMap(map);
        console.log('executiveReportMap:', map);
      }
    });
  }, [tenant]);

  const handleCopyTenantId = async (id: string) => {
    try {
      await navigator.clipboard.writeText(id);
      addToast('Tenant ID copied to clipboard', 'success', 2000);
    } catch (error) {
      console.error('Failed to copy tenant ID:', error);
      addToast('Failed to copy Tenant ID', 'error', 2000);
    }
  };

  const isTenantMasked = (id: string) => {
    return tenantPrivacyOverrides[id] ?? privacyMode;
  };

  const toggleTenantPrivacy = (id: string) => {
    setTenantPrivacyOverrides((prev) => ({
      ...prev,
      [id]: !(prev[id] ?? privacyMode),
    }));
  };

  const maskTenantName = (name: string, id: string) => {
    if (!isTenantMasked(id)) return name;
    return '*'.repeat(name.length);
  };

  // After "Select a tenant" (SelectTenantNotice), go back to the page that asked for one; otherwise the dashboard.
  const returnTo = (location.state as { from?: string } | null)?.from;
  const viewTenant = (selected: Tenant) => {
    setSelectedTenantId(selected.tenantId);
    navigate(returnTo || '/');
  };

  // Back to the signed-in user's own tenant (Globaladmin/reseller: the admin pages only, see tenantScope.ts).
  const isOwnTenantSelected = !selectedTenantId || selectedTenantId.toLowerCase() === String(tenantId ?? '').toLowerCase();
  const switchToOwnTenant = () => {
    if (tenantId) setSelectedTenantId(tenantId as string);
  };

  const display = {
    isMasked: isTenantMasked,
    maskName: maskTenantName,
    onToggleMask: toggleTenantPrivacy,
    onCopyId: handleCopyTenantId,
    onView: viewTenant,
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-white">
        <LoaderCircle className="animate-spin w-10 h-10 text-red-500" />
      </div>
    );
  }

  return (
    <div className="bg-black text-white p-6 rounded-md shadow-lg min-h-screen">
      {/* Header Section */}
      <div className="mb-8">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
          <div>
            {/* <h1 className="text-3xl font-bold mb-2">Customers</h1> */}
            <p className="text-gray-400">Manage your customer base</p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setPrivacyMode(!privacyMode)}
              className={`px-6 py-3 rounded-lg font-semibold transition-all flex items-center gap-2 ${
                privacyMode
                  ? 'bg-red-600 hover:bg-red-700 text-white'
                  : 'bg-zinc-800 hover:bg-zinc-700 text-gray-300 border border-zinc-700'
              }`}
              title={privacyMode ? 'Disable privacy mode' : 'Enable privacy mode'}
              aria-label={privacyMode ? 'Disable global privacy mode' : 'Enable global privacy mode'}
            >
              {privacyMode ? <FaEyeSlash size={16} /> : <FaEye size={16} />}
              <span>{privacyMode ? 'Privacy On' : 'Privacy Off'}</span>
            </button>
            {ROLES_ALLOWED_TO_CREATE.includes(role as string) && (
              <button
                className="px-6 py-3 bg-red-600 hover:bg-red-700 text-white rounded-lg font-semibold transition-all"
                onClick={() => navigate('/customers/CreateCustomer')}
              >
                + Create New Customer
              </button>
            )}
          </div>
        </div>

        {/* Search Bar */}
        <div className="relative">
          <input
            type="text"
            placeholder="Search by name, ID, or status..."
            className="w-full px-6 py-4 bg-zinc-900 text-white rounded-xl border border-zinc-700 focus:border-red-500 focus:ring-2 focus:ring-red-500/50 transition-all outline-none text-lg"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {search && (
            <button
              onClick={() => setSearch('')}
              className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white transition-colors bg-zinc-800 rounded-full p-2"
              aria-label="Clear search"
            >
              ✕
            </button>
          )}
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 mb-6">
        <div className="bg-zinc-900 p-6 rounded-xl border border-zinc-700 hover:border-red-500/50 transition-all">
          <div className="text-gray-400 text-sm mb-2">Total Customers</div>
          <div className="text-3xl font-bold">{filteredData.length}</div>
        </div>

        <div className="bg-zinc-900 p-6 rounded-xl border border-zinc-700 hover:border-red-500/50 transition-all">
          <div className="text-gray-400 text-sm mb-2">Avg Compliance</div>
          <div className="flex items-baseline gap-2">
            <div className="text-3xl font-bold text-red-400">
              {filteredData.length > 0
                ? (() => {
                    const totalAgents = filteredData.reduce((sum, t) => sum + (t.totalAgentCount || 0), 0);
                    const compliantAgents = filteredData.reduce((sum, t) => sum + (t.compliantAgentCount || 0), 0);
                    return totalAgents > 0 ? ((compliantAgents / totalAgents) * 100).toFixed(2) + '%' : 'N/A';
                  })()
                : 'N/A'}
            </div>
          </div>
          <div className="mt-2 w-full bg-zinc-800 rounded-full h-2">
            <div
              className="bg-red-500 h-2 rounded-full transition-all"
              style={{
                width:
                  filteredData.length > 0
                    ? (() => {
                        const totalAgents = filteredData.reduce((sum, t) => sum + (t.totalAgentCount || 0), 0);
                        const compliantAgents = filteredData.reduce((sum, t) => sum + (t.compliantAgentCount || 0), 0);
                        return totalAgents > 0 ? `${((compliantAgents / totalAgents) * 100).toFixed(2)}%` : '0%';
                      })()
                    : '0%',
              }}
            ></div>
          </div>
        </div>

        <div className="bg-zinc-900 p-6 rounded-xl border border-zinc-700 hover:border-red-500/50 transition-all">
          <div className="text-gray-400 text-sm mb-2">Total Devices</div>
          <div className="text-3xl font-bold">{filteredData.reduce((sum, t) => sum + (t.totalAgentCount || 0), 0).toLocaleString()}</div>
        </div>

        <div className="bg-zinc-900 p-6 rounded-xl border border-zinc-700 hover:border-red-500/50 transition-all">
          <div className="text-gray-400 text-sm mb-2">Licenses Bought</div>
          <div className="text-3xl font-bold text-white">
            {filteredData.reduce((sum, t) => sum + (t.totalLicenses || 0), 0).toLocaleString()}
          </div>
        </div>

        <div className="bg-zinc-900 p-6 rounded-xl border border-zinc-700 hover:border-red-500/50 transition-all">
          <div className="text-gray-400 text-sm mb-2">Licenses In Use</div>
          <div className="text-3xl font-bold text-gray-300">
            {filteredData.reduce((sum, t) => sum + (t.totalAgentCount || 0), 0).toLocaleString()}
          </div>
          <div className="text-xs text-gray-500 mt-1">
            {(() => {
              const bought = filteredData.reduce((sum, t) => sum + (t.totalLicenses || 0), 0);
              const inUse = filteredData.reduce((sum, t) => sum + (t.totalAgentCount || 0), 0);
              return bought > 0 ? `${Math.round((inUse / bought) * 100)}% utilized` : '0% utilized';
            })()}
          </div>
        </div>

        <div className="bg-zinc-900 p-6 rounded-xl border border-zinc-700 hover:border-red-500/50 transition-all">
          <div className="text-gray-400 text-sm mb-2">Available Licenses</div>
          <div className="text-3xl font-bold text-gray-300">
            {(() => {
              const bought = filteredData.reduce((sum, t) => sum + (t.totalLicenses || 0), 0);
              const inUse = filteredData.reduce((sum, t) => sum + (t.totalAgentCount || 0), 0);
              return Math.max(0, bought - inUse).toLocaleString();
            })()}
          </div>
        </div>
      </div>

      {/* Hierarchical Tree View */}
      <div className="space-y-6">
        {/* Global Tenant Section */}
        {/* Root Tenant Section (if available) */}
        {rootTenant && (
          <div className="bg-zinc-900 rounded-xl  overflow-hidden">
            {/* Root Tenant Header */}
            <div className="p-6 cursor-pointer hover:bg-zinc-800 transition-all" onClick={() => setExpandedGlobal(!expandedGlobal)}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div>
                    <div className="flex items-center gap-3">
                      <h2 className="text-2xl font-bold">{maskTenantName(rootTenant.tenantName, rootTenant.tenantId)}</h2>
                      <button
                        type="button"
                        className="text-gray-400 hover:text-white transition-colors"
                        title={isTenantMasked(rootTenant.tenantId) ? 'Show tenant name' : 'Hide tenant name'}
                        aria-label={isTenantMasked(rootTenant.tenantId) ? 'Show tenant name' : 'Hide tenant name'}
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleTenantPrivacy(rootTenant.tenantId);
                        }}
                      >
                        {isTenantMasked(rootTenant.tenantId) ? <FaEye size={16} /> : <FaEyeSlash size={16} />}
                      </button>
                    </div>
                    <div
                      className="font-mono text-xs text-gray-500 mt-1 cursor-pointer hover:text-red-400"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleCopyTenantId(rootTenant.tenantId);
                      }}
                    >
                      {rootTenant.tenantId}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span
                    className={`inline-flex items-center px-4 py-2 rounded-full text-sm font-semibold border ${getStatusColor(
                      rootTenant.status,
                    )}`}
                  >
                    {rootTenant.status}
                  </span>
                  <span className={`h-4 w-4 rounded-full ${rootTenant.isActive ? 'bg-green-500' : 'bg-red-600'}`}></span>
                  {isOwnTenantSelected ? (
                    <span className="rounded-full border border-zinc-700 px-3 py-1 text-xs font-semibold text-gray-400">
                      Current tenant
                    </span>
                  ) : (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        switchToOwnTenant();
                      }}
                      className="rounded-lg bg-zinc-700 px-4 py-2 text-sm font-medium text-white transition-all hover:bg-red-600"
                      title="Go back to your own tenant"
                    >
                      Switch to this tenant
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Resellers (Globaladmin): each reseller's tenants in a red-bordered box */}
        {grouping && grouping.resellers.length > 0 && (
          <div className="space-y-4">
            <div className="flex items-baseline gap-3">
              <h2 className="text-lg font-semibold text-white">Resellers</h2>
              <span className="text-sm text-gray-400">
                {grouping.resellers.length} {grouping.resellers.length === 1 ? 'reseller' : 'resellers'} ·{' '}
                {grouping.resellers.reduce((count, group) => count + group.tenants.length, 0)} tenants
              </span>
            </div>
            {grouping.resellers.map((group) => (
              <ResellerGroupBox
                key={group.reseller.tenantId}
                group={group}
                expanded={isResellerExpanded(group.reseller.tenantId)}
                onToggle={() => toggleReseller(group.reseller.tenantId)}
                {...display}
              />
            ))}
          </div>
        )}

        {groupByReseller && directoryFailed && (
          <div className="rounded-lg border border-zinc-700 bg-zinc-900 px-4 py-3 text-sm text-gray-400">
            Couldn't load which tenants belong to which reseller, so all tenants are listed together.
          </div>
        )}

        {/* Tenant Cards Grid and Pagination: tenants under no reseller when grouped, every tenant otherwise */}
        {currentData.length > 0 && (
          <div className="pb-6 border-t border-zinc-800">
            {grouping && <h2 className="mt-6 mb-2 text-lg font-semibold text-white">tenants</h2>}
            <div className="mb-4 text-sm text-gray-400">
              Showing {currentData.length} of {pagedTenants.length} {grouping ? 'tenants not managed by a reseller' : 'tenants'}
            </div>

            <div className="flex flex-col gap-3">
              {currentData.map((item) => (
                <TenantRow key={item.tenantId} tenant={item} {...display} />
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex justify-center mt-8 space-x-2 items-center bg-zinc-800 p-4 rounded-xl border border-zinc-700">
                {/* Previous Button */}
                <button
                  onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  className={`px-6 py-3 rounded-lg font-medium transition-all ${
                    currentPage === 1 ? 'bg-zinc-700 text-gray-600 cursor-not-allowed' : 'bg-zinc-700 text-white hover:bg-red-600'
                  }`}
                >
                  ← Previous
                </button>

                {/* Page Numbers */}
                <div className="flex space-x-2">
                  {Array.from({ length: totalPages }, (_, i) => (
                    <button
                      key={i}
                      onClick={() => setCurrentPage(i + 1)}
                      className={`px-4 py-3 rounded-lg font-medium transition-all ${
                        currentPage === i + 1 ? 'bg-red-600 text-white' : 'bg-zinc-700 text-white hover:bg-zinc-600'
                      }`}
                    >
                      {i + 1}
                    </button>
                  ))}
                </div>

                {/* Next Button */}
                <button
                  onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                  className={`px-6 py-3 rounded-lg font-medium transition-all ${
                    currentPage === totalPages ? 'bg-zinc-700 text-gray-600 cursor-not-allowed' : 'bg-zinc-700 text-white hover:bg-red-600'
                  }`}
                >
                  Next →
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomerBase;
